import { GamificationLeader, GamificationLeaderFilter, SDK } from "@axl-dev/school-sdk";
import { Badge, Modal, Spin, theme } from "antd";
import { useEffect, useMemo, useState } from "react";

import { useLoc } from "@app/shared/lib/hooks";
import { GamificationLeaderTable } from "@app/pages/gamification/components/leader/GamificationLeaderTable/GamificationLeaderTable";
import { UserAvatar } from "@app/shared/ui/UserAvatar";
import { Image } from "@app/shared/ui";
import { generateBtnHoverCss, parseBtnHoverProps, BtnHoverProps, sanitizeCssValue as sc } from "@app/widgets/shared/btnHoverCss";

export type LeaderboardViewMode = "podium" | "table";

export type LeaderboardWidgetProps = BtnHoverProps & {
  sdk: SDK;
  cardBgColor: string;
  cardBorderColor: string;
  cardBorderRadius: string;
  cardPadding: string;
  titleFontSize: string;
  titleFontFamily: string;
  titleFontWeight: string;
  titleTextColor: string;
  titleTextAlign: string;
  viewMode: LeaderboardViewMode;
  useMedalColors: boolean;
  leaderBorderColor: string;
  leaderAvatarSize: string;
  leaderNameColor: string;
  leaderScoreColor: string;
  leaderPlaceFontSize: string;
  leaderPlaceColor: string;
  leaderPlaceBgColor: string;
  leaderShowAchievements: boolean;
  leaderColumns: number;
  showFullLeaderboardBtn: boolean;
  emptyText: string;
  emptyTextColor: string;
  emptyTextFontSize: string;
  btnText: string;
  btnBgColor: string;
  btnTextColor: string;
  btnBorderRadius: string;
  btnFontSize: string;
  title?: string;
  prefetchedLeaders?: GamificationLeader[];
};

export const parseLeaderboardProps = (container: HTMLElement): Omit<LeaderboardWidgetProps, "sdk"> => {
  const attr = (name: string, fallback = "") => container.getAttribute(name) || fallback;
  return {
    cardBgColor: attr("data-card-bg-color", "#ffffff"),
    cardBorderColor: attr("data-card-border-color", "#f0f0f0"),
    cardBorderRadius: attr("data-card-border-radius", "8px"),
    cardPadding: attr("data-card-padding", "20px"),
    titleFontSize: attr("data-title-font-size", "20px"),
    titleFontFamily: attr("data-title-font-family"),
    titleFontWeight: attr("data-title-font-weight", "600"),
    titleTextColor: attr("data-title-text-color"),
    titleTextAlign: attr("data-title-text-align", "left"),
    viewMode: (attr("data-view-mode", "podium") as LeaderboardViewMode),
    useMedalColors: attr("data-use-medal-colors", "true") === "true",
    leaderBorderColor: attr("data-leader-border-color", "#1890ff"),
    leaderAvatarSize: attr("data-leader-avatar-size", "85px"),
    leaderNameColor: attr("data-leader-name-color", "#333333"),
    leaderScoreColor: attr("data-leader-score-color", "#52c41a"),
    leaderPlaceFontSize: attr("data-leader-place-font-size", "12px"),
    leaderPlaceColor: attr("data-leader-place-color", "#ffffff"),
    leaderPlaceBgColor: attr("data-leader-place-bg-color", "#1890ff"),
    leaderShowAchievements: attr("data-leader-show-achievements", "true") === "true",
    leaderColumns: parseInt(attr("data-leader-columns", "3")) || 3,
    showFullLeaderboardBtn: attr("data-show-full-leaderboard-btn", "true") === "true",
    emptyText: attr("data-empty-text"),
    emptyTextColor: attr("data-empty-text-color", "#aaaaaa"),
    emptyTextFontSize: attr("data-empty-text-font-size", "14px"),
    btnText: attr("data-btn-text"),
    btnBgColor: attr("data-btn-bg-color", "#1890ff"),
    btnTextColor: attr("data-btn-text-color", "#ffffff"),
    btnBorderRadius: attr("data-btn-border-radius", "5px"),
    btnFontSize: attr("data-btn-font-size", "14px"),
    title: attr("data-title") || undefined,
    ...parseBtnHoverProps(container),
  };
};

const HeaderLink: React.FC<{ text: string; onClick: () => void; color?: string; btnBgColor?: string; btnTextColor?: string }> = ({ text, onClick, color, btnBgColor, btnTextColor }) => {
  const { token } = theme.useToken();
  return (
    <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: "12px" }}>
      <a
        className="ez-cab-btn ez-leaderboard-btn"
        onClick={(e) => { e.preventDefault(); onClick(); }}
        href="#"
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: "6px",
          fontWeight: 500,
          color: btnTextColor || color || token.colorTextLightSolid,
          background: btnBgColor || token.colorPrimary,
          padding: "6px 14px",
          textDecoration: "none",
          cursor: "pointer",
        }}
      >
        {text} <span style={{ fontSize: "12px" }}>→</span>
      </a>
    </div>
  );
};

export const LeaderboardWidget: React.FC<LeaderboardWidgetProps> = (props) => {
  const loc = useLoc();
  const { token } = theme.useToken();
  const [leaders, setLeaders] = useState<GamificationLeader[]>(props.prefetchedLeaders || []);
  const [loading, setLoading] = useState(!props.prefetchedLeaders);
  const [showFull, setShowFull] = useState(false);

  const scopeId = useMemo(() => `ez-lb-${Math.random().toString(36).slice(2, 8)}`, []);
  const hoverCss = useMemo(() => generateBtnHoverCss(`.${scopeId}`, "button, a", props), [scopeId, props.btnHoverEffect, props.btnHoverBgColor, props.btnHoverTextColor, props.btnHoverShadow, props.btnHoverScale, props.btnHoverTransitionDuration]);
  const themeCss = useMemo(() => `.${scopeId} .ez-leaderboard-panel{border:1px solid ${sc(props.cardBorderColor || token.colorBorder)};border-radius:${sc(props.cardBorderRadius || "8px")};background-color:${sc(props.cardBgColor || token.colorBgElevated)};padding:${sc(props.cardPadding || "20px")};}` + `.${scopeId} .ez-leaderboard-btn{border-radius:${sc(props.btnBorderRadius || "5px")};font-size:${sc(props.btnFontSize || "14px")};}`, [scopeId, props.cardBorderColor, props.cardBorderRadius, props.cardBgColor, props.cardPadding, props.btnBorderRadius, props.btnFontSize, token]);

  useEffect(() => {
    if (props.prefetchedLeaders) { setLeaders(props.prefetchedLeaders); setLoading(false); return; }
    let cancelled = false;
    props.sdk.api.gamification.fetchLeaders(new GamificationLeaderFilter({ take: props.leaderColumns || 3 })).then((res: any) => {
      if (!cancelled && res.success && res.body?.items) setLeaders(res.body.items);
    }).finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [props.sdk, props.prefetchedLeaders, props.leaderColumns]);

  const cardStyle: React.CSSProperties = {
    boxShadow: "0 1px 2px 0 rgba(0,0,0,0.03)",
    boxSizing: "border-box",
    maxWidth: "100%",
    overflow: "hidden",
  };

  const viewMode: LeaderboardViewMode = props.viewMode === "table" ? "table" : "podium";
  const leaderCount = Math.max(1, Math.min(10, props.leaderColumns || 3));
  const baseAvatarSize = parseInt(props.leaderAvatarSize) || 85;
  const isEmpty = leaders.length === 0;

  const items: Array<{
    id: string;
    place: number;
    name: string;
    score: number;
    contact: any;
    achievements?: any[];
    placeholder?: boolean;
  }> = isEmpty
    ? Array.from({ length: leaderCount }, (_, i) => ({
        id: `ph-${i + 1}`,
        place: i + 1,
        name: "—",
        score: 0,
        contact: null,
        achievements: [],
        placeholder: true,
      }))
    : [...leaders]
        .slice(0, leaderCount)
        .map((l) => ({
          id: String(l.id),
          place: l.place,
          name: l.contact?.fullName || loc.word("Unnamed"),
          score: l.score,
          contact: l.contact,
          achievements: l.achievements,
        }));

  const medalColors: Record<number, { border: string; ribbonBg: string; ribbonText: string }> = {
    1: { border: "#FFD666", ribbonBg: "#FFD666", ribbonText: "#FAAD14" },
    2: { border: "#E8E8E8", ribbonBg: "#F5F5F5", ribbonText: "#8C8C8C" },
    3: { border: "#FFD591", ribbonBg: "#FFD591", ribbonText: "#D46B08" },
  };
  const customColors = {
    border: props.leaderBorderColor || token.colorPrimary,
    ribbonBg: props.leaderPlaceBgColor || token.colorPrimary,
    ribbonText: props.leaderPlaceColor || token.colorTextLightSolid,
  };
  const colorsFor = (place: number) => (props.useMedalColors && medalColors[place]) ? medalColors[place] : customColors;

  const ordered = viewMode === "podium" && items.length === 3
    ? items.slice().sort((a, b) => {
        const orderMap: Record<number, number> = { 2: 0, 1: 1, 3: 2 };
        const ao = orderMap[a.place] ?? a.place + 10;
        const bo = orderMap[b.place] ?? b.place + 10;
        return ao - bo;
      })
    : items.slice().sort((a, b) => a.place - b.place);

  return (
    <div className={scopeId}>
      <style>{hoverCss}{themeCss}</style>
      {props.title && (
        <div style={{
          fontSize: props.titleFontSize || "20px",
          fontWeight: (props.titleFontWeight as any) || 600,
          fontFamily: props.titleFontFamily || undefined,
          color: props.titleTextColor || undefined,
          textAlign: (props.titleTextAlign as any) || "left",
          marginBottom: "12px",
        }}>{props.title}</div>
      )}
      <div className="ez-leaderboard-panel" style={cardStyle}>
        {loading ? (
          <div style={{ display: "flex", justifyContent: "center", padding: "20px" }}><Spin /></div>
        ) : (
          <>
            {props.showFullLeaderboardBtn && (
              <HeaderLink
                text={props.btnText || loc.word("Gamification.leaders.showAll", { default: "Show leaderboard" })}
                onClick={() => setShowFull(true)}
                color={props.leaderScoreColor}
                btnBgColor={props.btnBgColor}
                btnTextColor={props.btnTextColor}
              />
            )}
            {isEmpty ? (
              <div style={{ textAlign: "center", padding: "24px", color: props.emptyTextColor || token.colorTextTertiary, fontSize: props.emptyTextFontSize || "14px" }}>
                {props.emptyText || loc.word("Gamification.leaders.empty", { default: "No leaders yet" })}
              </div>
            ) : viewMode === "table" ? (
              <div style={{ display: "flex", flexDirection: "column" }}>
                {ordered.map((l) => {
                  const colors = colorsFor(l.place);
                  const nameSize = undefined;
                  const scoreSize = undefined;
                  return (
                    <div key={l.id} className="ez-cab-card ez-leaderboard-card" style={{ display: "flex", alignItems: "center", gap: "12px", padding: "10px 0", borderBottom: `1px solid ${token.colorBorderSecondary}`, opacity: l.placeholder ? 0.6 : 1 }}>
                      <div style={{ minWidth: "32px", height: "32px", padding: "0 10px", borderRadius: "16px", backgroundColor: colors.ribbonBg, color: colors.ribbonText, fontSize: props.leaderPlaceFontSize || "14px", fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>{l.place}</div>
                      <div style={{ borderRadius: "50%", border: `2px solid ${colors.border}`, flexShrink: 0, lineHeight: 0 }}>
                        {l.placeholder
                          ? <div style={{ width: 36, height: 36, borderRadius: "50%", background: token.colorFillSecondary }} />
                          : <UserAvatar user={l.contact} size={36} />}
                      </div>
                      <div className="lb-name ez-cab-title ez-leaderboard-title" style={{ flex: 1, minWidth: 0, fontSize: nameSize, fontWeight: 600, color: props.leaderNameColor || token.colorText, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{l.name}</div>
                      {props.leaderShowAchievements && l.achievements && l.achievements.length > 0 && (
                        <div style={{ display: "flex", gap: "4px", flexShrink: 0 }}>
                          {l.achievements.slice(0, 3).map((a: any) => (
                            <Image key={a.id} cloudKey={a.imageCloudKey} width={20} height={20} fit="fill" className="br-xxs" resize={{ w: 20, h: 20 }} />
                          ))}
                          {l.achievements.length > 3 && <span style={{ fontSize: "11px", color: token.colorTextSecondary, alignSelf: "center" }}>+{l.achievements.length - 3}</span>}
                        </div>
                      )}
                      <div className="lb-score ez-leaderboard-score" style={{ fontSize: scoreSize, fontWeight: 700, color: props.leaderScoreColor || token.colorSuccess, flexShrink: 0 }}>{l.score}</div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div style={{ display: "flex", justifyContent: "space-around", alignItems: "flex-end", flexWrap: "wrap", gap: "20px" }}>
                {ordered.map((l) => {
                  const isFirst = l.place === 1;
                  const pyramidBoost = isFirst;
                  const size = pyramidBoost ? Math.round(baseAvatarSize * 1.35) : baseAvatarSize;
                  const colors = colorsFor(l.place);
                  const nameSize = pyramidBoost ? "16px" : undefined;
                  const scoreSize = pyramidBoost ? "28px" : undefined;
                  return (
                    <div key={l.id} className="ez-cab-card ez-leaderboard-card" style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "6px", opacity: l.placeholder ? 0.6 : 1 }}>
                      <Badge.Ribbon
                        text={l.place}
                        color={colors.ribbonBg}
                        style={{ fontSize: props.leaderPlaceFontSize || "12px", color: colors.ribbonText, fontWeight: 600 }}
                      >
                        {l.placeholder ? (
                          <div style={{ width: size, height: size, borderRadius: "50%", border: `4px solid ${colors.border}`, background: token.colorFillSecondary }} />
                        ) : (
                          <div style={{ borderRadius: "50%", border: `4px solid ${colors.border}`, lineHeight: 0 }}>
                            <UserAvatar user={l.contact} size={size} />
                          </div>
                        )}
                      </Badge.Ribbon>
                      <div className="lb-name ez-cab-title ez-leaderboard-title" style={{ fontSize: nameSize, fontWeight: 600, color: props.leaderNameColor || token.colorText, marginTop: "4px", textAlign: "center", maxWidth: "100%", wordBreak: "break-word" }}>{l.name}</div>
                      <div className="lb-score ez-leaderboard-score" style={{ fontSize: scoreSize, fontWeight: 700, color: props.leaderScoreColor || token.colorSuccess }}>{l.score}</div>
                      {props.leaderShowAchievements && l.achievements && l.achievements.length > 0 && (
                        <div style={{ display: "flex", gap: "4px", flexWrap: "wrap", justifyContent: "center" }}>
                          {l.achievements.slice(0, 5).map((a: any) => (
                            <Image key={a.id} cloudKey={a.imageCloudKey} width={24} height={24} fit="fill" className="br-xxs" resize={{ w: 24, h: 24 }} />
                          ))}
                          {l.achievements.length > 5 && <span style={{ fontSize: "11px", color: token.colorTextSecondary }}>+{l.achievements.length - 5}</span>}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}
      </div>
      <Modal
        open={showFull}
        title={loc.word("Gamification.leaderboard", { default: "Leaderboard" })}
        okButtonProps={{ style: { display: "none" } }}
        onCancel={() => setShowFull(false)}
        width={1000}
        destroyOnHidden
      >
        <GamificationLeaderTable />
      </Modal>
    </div>
  );
};
